import React, { useEffect, useState } from 'react';
import { WebViewNavigation } from 'react-native-webview';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import Webview from '@/components/Webview';
import { Box } from '@/components/lib/components';
import { useDeposit } from '@/feature/deposit/hooks/use-deposit';
import { usePayByBankDeepLink } from '@/feature/devsettings/hooks/use-pay-by-bank-deeplink';
import { DepositStackParamList } from '@/navigation/types';
import { getWalletErrorCode } from '@/utils/get-wallet-error-code';

import { DEPOSIT_STATUS_UNIVERSAL_LINK_URL, PaymentMethodTypes } from '../const';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { PayPalStatus } from '../utils';

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'PayByBankDepositWebViewModal'>;

export const PayByBankDepositWebView = ({ route }: ScreenProps) => {
    const { currency = 'USD' } = route.params || {};
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackageId = useDepositStatusStore(state => state.coinPackageId);
    const selectedPaymentMethodType = PaymentMethodTypes.PaysafePayByBank;
    const navigation = useNavigation<NativeStackNavigationProp<DepositStackParamList>>();
    const [uri, setUri] = useState<string | undefined>();
    const { mutate: deposit } = useDeposit();

    usePayByBankDeepLink();

    useEffect(() => {
        deposit(
            {
                type: PaymentMethodTypes.PaysafePayByBank,
                currency,
            },
            {
                onSuccess: data => setUri(data.payment_url),
                onError: error => {
                    navigation.navigate('FailedDepositModal', {
                        paymentProvider: selectedPaymentMethodType,
                        errorCode: getWalletErrorCode(error),
                    });
                },
            }
        );
    }, [currency, deposit, navigation, selectedAmount, selectedPaymentMethodType, coinPackageId]);

    const handleNavigationChange = (navState: WebViewNavigation) => {
        if (navState.url.startsWith(DEPOSIT_STATUS_UNIVERSAL_LINK_URL)) {
            const url = new URL(navState.url);
            const params = new URLSearchParams(url.search);

            const paymentProvider = params.get('paymentProvider') || '';
            const currentStatus = params.get('currentStatus') || '';

            if (currentStatus === PayPalStatus.SUCCESS) {
                navigation.navigate('SuccessfulDepositModal', {
                    paymentProvider: paymentProvider,
                });
            } else {
                navigation.navigate('FailedDepositModal', {
                    paymentProvider: paymentProvider,
                });
            }
        }
    };

    if (!uri) {
        return (
            <Box alignItems="center" justifyContent="center" height="100%">
                <LoadingSpinner />
            </Box>
        );
    }

    return (
        <Screen>
            <ScreenNavBar title={'Deposit'} closeIconMode="back" />
            <Webview
                source={{ uri }}
                allowsInlineMediaPlayback
                mediaPlaybackRequiresUserAction={false}
                startInLoadingState
                onNavigationStateChange={handleNavigationChange}
                renderLoading={() => (
                    <Box alignItems="center" justifyContent="center" height="100%">
                        <LoadingSpinner />
                    </Box>
                )}
            />
        </Screen>
    );
};
